import { useState } from 'react';
import styled from 'styled-components';

import {
  SvgIcon,
  ThemeModeType,
  useMatchMedia,
  FlexRow,
} from '@make-software/cspr-ui';

import { NavLogo } from '../nav-logo';
import { SkeletonTheme } from 'react-loading-skeleton';
import { BUY_CSPR_PATH, HOME_PATH } from '../../../router/paths';
import { useLocation } from 'react-router';
import {
  BuyCSPRMenuItem,
  ClickUI,
  CopyHashMenuItem,
  Lang,
} from '@make-software/csprclick-ui';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { setMode } from '../../../redux/theme';
import { ActiveLanguages, ChainNames } from '../../../domain';
import { useCurrencies } from './use-currencies';
import { AccountActivityExportMenuItem } from './account-activity-export-menu-item';
import { useTranslation } from 'react-i18next';
import { ViewAccountMenuItem } from './view-account-menu-item';
import { MainNavigationMenu } from './main-navigation-menu';
import { AssociatedKeysMenuItem } from './associated-keys-menu-item';
import SearchWithAdBanner from '../search-with-ad-banner';
import { NavMenuContext } from './nav-menu.context';
import { useGetActiveAccountDelegations } from '../../../hooks/event-store-hooks';

const Container = styled.div<{ isHome?: boolean }>(({ theme, isHome }) =>
  theme.withMedia({
    background: theme.styleguideColors.backgroundTertiary,
    display: 'flex',
    flexDirection: ['column'],
    justifyContent: ['center'],
    alignItems: ['center'],
    padding: isHome ? [0, '0 0 165px', '0 0 125px'] : [0],
    width: '100%',
  })
);

const ClickContainer = styled.div(({ theme }) =>
  theme.withMedia({
    background: theme.styleguideColors.backgroundTertiary,
    width: '100%',
    maxWidth: 1176,
    margin: '0 auto',
    padding: ['0px 16px', '0px 32px', '0px 56px', '0px'],
  })
);

const SettingsContainer = styled.div(({ theme }) =>
  theme.withMedia({
    background: theme.styleguideColors.backgroundTertiary,
    height: ['unset', 'unset', '40px'],
    width: '100%',
    display: 'flex',
    flexDirection: ['row', 'row', 'column'],
    justifyContent: ['center'],
    alignItems: ['center'],
  })
);

const NavBarContainer = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    flexDirection: ['row'],
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: theme.maxWidth,
    width: '100%',
    padding: ['19px 16px', '19px 32px', '0px 56px', '0px'],
    height: ['auto', 'auto', 'auto', '72px'],
    margin: [0],
  })
);

const StyledHamburgerButton = styled(SvgIcon)(({ theme }) => ({
  color: theme.styleguideColors.contentTertiary,
  ':hover, :active': {
    color: theme.styleguideColors.contentOnFill,
  },
}));

const UaIcon = styled(SvgIcon)(() => ({
  margin: '0 0 6px 8px',
}));

const HamburgerButton = (props) => (
  <StyledHamburgerButton
    src="assets/icons/ic-menu.svg"
    alt={props.t('Hamburger menu icon')}
    size={16}
    {...props}
  />
);

const ChainNamesMap = {
  [ChainNames.MAINNET]: 'Mainnet',
  [ChainNames.TESTNET]: 'Testnet',
};

const NETWORKS = [
  { title: 'Mainnet', url: 'https://cspr.live' },
  { title: 'Testnet', url: 'https://testnet.cspr.live' },
];

const CSPRClickBar = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const mode = useSelector((state: RootState) => state.theme.mode);
  const activePublicKey = useSelector(
    (state: RootState) => state.wallet.activePublicKey
  );

  useGetActiveAccountDelegations(
    activePublicKey
      ? {
          publicKey: activePublicKey,
          includes:
            'bidder,validator_account_info,cspr_name,validator_cspr_name',
          page_size: 250,
        }
      : null
  );

  const { currentCurrency, currencies, onChangeCurrency } = useCurrencies();

  const handleThemeSwitch = () => {
    const nextMode =
      mode === ThemeModeType.light ? ThemeModeType.dark : ThemeModeType.light;

    dispatch(setMode(nextMode));

    window.gtag('event', 'click', {
      event_category: 'dark_mode',
      event_label: `${nextMode} mode is selected`,
    });
  };

  const handleLanguageSwitch = (language) => {
    i18n.changeLanguage(language);
  };

  const handleNetworkSwitch = (network) => {
    window.location.href = NETWORKS.find((n) => n.title === network)!.url;
  };

  const currencySettings =
    currencies.length > 1
      ? {
          currencies: currencies,
          onChangeCurrency: onChangeCurrency,
          currentCurrency: currentCurrency,
        }
      : undefined;

  const networkSettings =
    config.network_name === ChainNames.MAINNET ||
    config.network_name === ChainNames.TESTNET
      ? {
          networks: NETWORKS,
          onNetworkSwitch: handleNetworkSwitch,
          currentNetwork: ChainNamesMap[config.network_name],
        }
      : undefined;

  const accountMenuItems = config.with_cspr_live_plus
    ? [
        <ViewAccountMenuItem key={0} />,
        <CopyHashMenuItem key={1} />,
        <AccountActivityExportMenuItem key={3} />,
        <AssociatedKeysMenuItem key={4} />,
      ]
    : [
        <ViewAccountMenuItem key={0} />,
        <CopyHashMenuItem key={1} />,
        <AssociatedKeysMenuItem key={2} />,
      ];

  if (config.with_with_buy_cspr_button) {
    accountMenuItems.push(<BuyCSPRMenuItem key={5} />);
  }

  const topBarSettings = {
    onThemeSwitch: handleThemeSwitch,
    accountMenuItems: accountMenuItems,
    languageSettings: {
      languages: ActiveLanguages,
      currentLanguage: i18n.language as Lang,
      creditsUrl: 'https://cspr.live/credits',
      contributeUrl: 'https://translate.cspr.live/',
      onChangeLanguage: handleLanguageSwitch,
    },
    currencySettings: currencySettings,
    networkSettings: networkSettings,
  };

  return (
    <ClickContainer>
      <ClickUI topBarSettings={topBarSettings} themeMode={mode} />
      <div className={'associated-keys-spotlight-target'} />
    </ClickContainer>
  );
};

const PageHeaderWithClick = ({ ...props }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [menuVisible, setMenuVisible] = useState(false);
  const isHome = pathname === HOME_PATH || pathname === BUY_CSPR_PATH;

  const onMobile = (
    <Container isHome={isHome} {...props}>
      <NavBarContainer>
        <FlexRow align={'end'}>
          <NavLogo />
          <UaIcon
            src={'assets/icons/ic-ua-heart.svg'}
            alt={t('Heart with ukrainian flag')}
            size={17}
          />
        </FlexRow>
        <HamburgerButton
          onClick={() => {
            setMenuVisible((visible) => !visible);
          }}
          t={t}
        />
      </NavBarContainer>
      <SearchWithAdBanner isHome={isHome} />
      {!!menuVisible && <MainNavigationMenu isHome={isHome} />}
    </Container>
  );

  const onTablet = (
    <Container isHome={isHome} {...props}>
      <NavBarContainer>
        <FlexRow align={'end'}>
          <NavLogo />
          <UaIcon
            src={'assets/icons/ic-ua-heart.svg'}
            size={17}
            alt={t('Heart with ukrainian flag')}
          />
        </FlexRow>
        <HamburgerButton
          onClick={() => {
            setMenuVisible((visible) => !visible);
          }}
          t={t}
        />
      </NavBarContainer>
      <SearchWithAdBanner isHome={isHome} />
      {!!menuVisible && <MainNavigationMenu isHome={isHome} />}
    </Container>
  );

  const onAbove = (
    <Container isHome={isHome} {...props}>
      <NavBarContainer>
        <FlexRow align={'end'}>
          <NavLogo />
          <UaIcon
            src={'assets/icons/ic-ua-heart.svg'}
            size={17}
            alt={t('Heart with ukrainian flag')}
          />
        </FlexRow>
        <MainNavigationMenu isHome={isHome} />
      </NavBarContainer>
      <SearchWithAdBanner isHome={isHome} />
    </Container>
  );

  const responsiveHeader = useMatchMedia(
    [onMobile, onTablet, onAbove],
    [menuVisible, isHome]
  );

  return (
    <div
      style={{
        width: '100%',
        ...(isHome ? { minHeight: '300px' } : { minHeight: '110px' }),
      }}
    >
      <SkeletonTheme baseColor="rgba(22,29,59)" highlightColor="#1B254A">
        <NavMenuContext.Provider
          value={{
            menuVisible: menuVisible,
            setMenuVisible: setMenuVisible,
          }}
        >
          <SettingsContainer>
            <CSPRClickBar />
          </SettingsContainer>
          {responsiveHeader}
        </NavMenuContext.Provider>
      </SkeletonTheme>
    </div>
  );
};

export default PageHeaderWithClick;
